import * as React from "react"
import {useEffect} from "react"

import {Field, FieldRenderProps, Form} from "@progress/kendo-react-form"
import {Checkbox, Input, InputChangeEvent} from "@progress/kendo-react-inputs"

import {Grid, GridCellProps, GridColumn, GridToolbar} from "@progress/kendo-react-grid"
import {
    addCustomerContact,
    editCustomerContact,
    fetchCustomerContact,
    setShowDeletePopover,
    setDeleteCustomercontactId,
} from "../../../containers/Customer/customerSlice"
import {RootState} from "../../../rootReducer"
import {useAppDispatch, useAppSelector} from "../../../store"
// import {CheckBoxEditableCell} from "../../../shared/CellEditorCheckBoxTemplate"
import {Button} from "@progress/kendo-react-buttons"
import {LoadingPanel} from "../../../shared/CommonItems"
import {BookingDetail, fetchCustomerContactReminderList} from "../../../containers/MotBooking/bookingSlice"

const FORM_DATA_INDEX = "formDataIndex"
const DATA_ITEM_KEY = "customerContactId"

const requiredValidator = (value: string) => (value.trim() ? "" : "The name is required")
const phonevalidator = (value: string) => {
    if (value.length === 0) {
        return undefined
    }
    return value.trim() ? "" : "Enter valid phone number"
}
const emailRegex = new RegExp(/\S+@\S+\.\S+/)
const emailValidator = (value: string) => {
    if (value.length === 0) {
        return undefined
    }
    return emailRegex.test(value) ? "" : "Please enter a vaild email."
}

const TextInputWithValidation = (fieldRenderProps: FieldRenderProps) => {
    const {validationMessage, visited, onChange, ...others} = fieldRenderProps
    return (
        <div>
            <Input onChange={onChange} required {...others} />
        </div>
    )
}

const CheckBoxCell = (fieldRenderProps: FieldRenderProps) => {
    const {value, onChange} = fieldRenderProps
    const {ref} = React.useContext(FormSubmitContext)
    return (
        <Checkbox
            disabled={ref.current?.values?.email.length === 0}
            defaultChecked={value}
            value={ref.current?.values?.email.length === 0 ? false : value}
            onChange={onChange}
        />
    )
}

const FormSubmitContext = React.createContext<{
    onSubmit: (event: React.SyntheticEvent<any, Event>) => void
    ref: any | null
}>({
    onSubmit: () => undefined,
    ref: null,
})

type actionUnion = "save" | "remove" | "add" | "addNew" | "delete"

const GridEditContext = React.createContext<{
    onRowAction: (options: {
        rowIndex: number
        operation: actionUnion
        dataItem?: any
    }) => void
    setEditIndex: (rowIndex: number | undefined) => void
    editIndex: number | undefined
}>({} as any)

const GridInlineFormRow = (props: {children: any; dataItem: any}) => {
    const cellFormRef = React.useRef(null)
    const {onRowAction, editIndex} = React.useContext(GridEditContext)
    const isInEdit = props.dataItem[FORM_DATA_INDEX] === editIndex
    const isNewItem = !props.dataItem[DATA_ITEM_KEY]

    const onSubmit = React.useCallback(
        e => {
            if(editIndex !== undefined) {
                isNewItem
                    ? onRowAction({rowIndex: editIndex, operation: "addNew", dataItem: e})
                    : onRowAction({rowIndex: editIndex, operation: "save", dataItem: e})
            }
        },
        [onRowAction, editIndex],
    )

    if (isInEdit) {
        return (
            <Form
                ref={cellFormRef}
                key={JSON.stringify(props.dataItem)}
                initialValues={props.dataItem}
                onSubmit={onSubmit}
                ignoreModified
                render={formRenderProps => {
                    return (
                        <FormSubmitContext.Provider value={{onSubmit: formRenderProps.onSubmit, ref: cellFormRef}}>
                            {props.children}
                        </FormSubmitContext.Provider>
                    )
                }}
            />
        )
    }

    return props.children
}

const CommandCell = (props: GridCellProps) => {
    const {onSubmit} = React.useContext(FormSubmitContext)
    const {onRowAction, setEditIndex, editIndex} = React.useContext(GridEditContext)

    const rowIndex = props.dataItem[FORM_DATA_INDEX]
    const isInEdit = rowIndex === editIndex
    const isNewItem = !props.dataItem[DATA_ITEM_KEY]

    const onRemoveClick = React.useCallback(
        e => {
            e.preventDefault()
            onRowAction({rowIndex, operation: "remove"})
            setEditIndex(undefined)
        },
        [rowIndex, onRowAction],
    )
    const onDeleteClick = React.useCallback(
        e => {
            e.preventDefault()
            onRowAction({
                rowIndex,
                operation: "delete",
            })
        },
        [rowIndex, onRowAction],
    )

    const onSaveClick = React.useCallback(
        e => {
            e.preventDefault()
            onSubmit(e)
        },
        [onSubmit],
    )
    const onNewAddClick = React.useCallback(
        e => {
            e.preventDefault()
            onSubmit(e)
        },
        [onSubmit],
    )

    const onEditClick = React.useCallback(
        e => {
            e.preventDefault()
            setEditIndex(rowIndex)
        },
        [rowIndex, setEditIndex],
    )

    const onCancelClick = React.useCallback(
        e => {
            e.preventDefault()
            setEditIndex(undefined)
        },
        [setEditIndex],
    )

    return isInEdit ? (
        <td className="k-command-cell">
            <Button fillMode="flat" themeColor={"primary"} onClick={isNewItem ? onNewAddClick : onSaveClick}>
                {isNewItem ? "Add" : "Update"}
            </Button>
            <Button fillMode="flat" themeColor={"primary"} onClick={isNewItem ? onRemoveClick : onCancelClick}>
                {isNewItem ? "Discard" : "Cancel"}
            </Button>
        </td>
    ) : (
        <td className="k-command-cell">
            <Button fillMode="flat" themeColor={"primary"} onClick={onEditClick}>
                Edit
            </Button>
            <Button fillMode="flat" themeColor={"error"} onClick={onDeleteClick}>
                Delete
            </Button>
        </td>
    )
}

const rowRender = (row: any, props: any) => {
    return <GridInlineFormRow dataItem={props.dataItem}>{row}</GridInlineFormRow>
}

export const CustomerContactAddEdit = ({
    customerId,
    enableEdit,
    booking,
    onBlur,
}: {
    customerId: number
    enableEdit: boolean
    booking: BookingDetail
    onBlur: () => void
}) => {
    const dispatch = useAppDispatch()
    const loadingContactDetails = useAppSelector((state: RootState) => state.containers.customer.customerContactloading)
    const customerContacts = useAppSelector((state: RootState) => state.containers.customer.customerContacts)

    // Form data index is used as an alternative to ID for rows after data operations
    const [dataState, setDataState] = React.useState(
        customerContacts.map((dataItem, idx) => ({
            ...dataItem,
            [FORM_DATA_INDEX]: idx,
        })),
    )
    const [editIndexState, setEditIndex] = React.useState<number | undefined>(undefined)

    useEffect(() => {
        setDataState(
            customerContacts.map((dataItem, idx) => ({
                ...dataItem,
                [FORM_DATA_INDEX]: idx,
            })),
        )
    }, [customerContacts])

    const isInitialRender = React.useRef(true)

    useEffect(() => {
        // Skip the effect on the initial render
        if (isInitialRender.current) {
            isInitialRender.current = false
            return
        }
        customerId > 0 && dispatch(fetchCustomerContactReminderList(booking, customerId))
    }, [customerContacts])

    useEffect(() => {
        dispatch(fetchCustomerContact(customerId))
    }, [customerId, dispatch])

    const onRowAction = React.useCallback(
        (options: {rowIndex: number; operation: actionUnion; dataItem?: any}) => {
            const newDataState = [...dataState]
            switch (options.operation) {
                case "remove":
                    newDataState.splice(0, 1)
                    break
                case "save":
                    options.dataItem.reminderEmail =
                        options.dataItem.email.trim().length === 0 ? false : options.dataItem.reminderEmail

                    const updateContact: any[] = dataState.map((x: any) => {
                        if (x.customerContactId === options.dataItem.customerContactId) {
                            return {
                                ...options.dataItem,
                            }
                        } else {
                            return x
                        }
                    })

                    setDataState(updateContact)
                    dispatch(editCustomerContact(options.dataItem))
                    setEditIndex(undefined)
                    break
                case "addNew":
                    options.dataItem.reminderEmail =
                        options.dataItem.email.trim().length === 0 ? false : options.dataItem.reminderEmail

                    const addNewContact: any[] = dataState.map((x: any) => {
                        if (x.customerContactId === options.dataItem.customerContactId) {
                            return {
                                ...options.dataItem,
                            }
                        } else {
                            return x
                        }
                    })
                    setDataState(addNewContact)
                    dispatch(addCustomerContact(options.dataItem))

                    setEditIndex(undefined)
                    break
                case "delete":
                    onBlur()
                    dispatch(setShowDeletePopover(true))
                    dispatch(setDeleteCustomercontactId(dataState[options.rowIndex].customerContactId))
                    setEditIndex(undefined)
                    break
                case "add":
                    if(editIndexState === undefined) {
                        newDataState.unshift({
                            inEdit: true,
                            customerContactId: 0,
                            customerId,
                            firstName: "",
                            lastName: "",
                            email: "",
                            phone: "",
                            primaryContact: false,
                            active: false,
                            [FORM_DATA_INDEX]: options.rowIndex,
                            reminderEmail: false,
                        })
                        setEditIndex(options.rowIndex)
                    }
                    break
                default:
            }
            setDataState(newDataState)
        },
        [dataState],
    )

    const ReminderEmailEditor = (props: GridCellProps) => {
        const {editIndex} = React.useContext(GridEditContext)
        const isInEdit = props.dataItem[FORM_DATA_INDEX] === editIndex
        
        const field = `${props.field}`;
        const value = `${props.dataItem[field]}`;

        return (
            <td>
                {isInEdit ? (
                    <Field
                        {...props}
                        component={CheckBoxCell}
                        value={value}
                        name={field}
                    />
                ) : (
                    <Checkbox disabled defaultChecked={props.dataItem[field]} />
                )}
            </td>
        )
    }

    const NameCell = (props: GridCellProps) => {
        const {editIndex} = React.useContext(GridEditContext)
        const isInEdit = props.dataItem[FORM_DATA_INDEX] === editIndex
        const validation =
            props.field === "firstName"
                ? requiredValidator
                : undefined || props.field === "phone"
                ? phonevalidator
                : undefined

        return (
            <td>
                {isInEdit ? (
                    <Field
                        component={TextInputWithValidation}
                        name={`${props.field}`}
                        validator={props.field === "email" ? emailValidator : validation}
                    />
                ) : (
                    props.dataItem[props.field || ""]
                )}
            </td>
        )
    }

    const onAddClick = React.useCallback(() => {
        onRowAction({rowIndex: dataState.length, operation: "add"})
    }, [onRowAction, dataState])

    return (
        <div className="position-relative">
            <div className="font-weight-bold text-muted">Contacts</div>
            <hr className="title-hr mt-0" />
            {loadingContactDetails && <LoadingPanel />}
            <GridEditContext.Provider value={{onRowAction, editIndex: editIndexState, setEditIndex}}>
                <Grid style={{height: "240px"}} data={dataState} dataItemKey={DATA_ITEM_KEY} rowRender={rowRender}>
                    {enableEdit && (
                        <GridToolbar>
                            <Button
                                title="Add Contact"
                                fillMode="flat"
                                themeColor={"primary"}
                                iconClass="fa fa-plus"
                                onClick={onAddClick}
                            >
                                New Contact
                            </Button>
                        </GridToolbar>
                    )}
                    <GridColumn title="First Name" field="firstName" cell={NameCell} width={120} />
                    <GridColumn title="Last Name" field="lastName" cell={NameCell} width={120} />
                    <GridColumn title="Email" field="email" cell={NameCell} />
                    <GridColumn title="Phone" field="phone" cell={NameCell} width={140} />
                    <GridColumn
                        field="reminderEmail"
                        title="ReminderEmail"
                        width={110}
                        cell={ReminderEmailEditor}
                    ></GridColumn>
                    {enableEdit && <GridColumn cell={CommandCell} width={220} />}
                </Grid>
            </GridEditContext.Provider>
        </div>
    )
}

export default CustomerContactAddEdit
